import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { FileText, Video, Users, PenLine, MessageCircle, TrendingUp } from "lucide-react";

const STEPS = [
  {
    phase: "Before the class",
    icon: FileText,
    title: "Pre-reading & prep sent out",
    desc: "Notes, the week's focus and any pre-reading land in your Google Classroom, so nobody walks in cold.",
  },
  {
    phase: "Before the class",
    icon: MessageCircle,
    title: "Ask anything, any time",
    desc: "Stuck on homework the night before? Text your tutor — 24/7 support means questions never wait a week.",
  },
  {
    phase: "In the class",
    icon: Users,
    title: "New content, taught properly",
    desc: "A small group of students, one tutor, fresh content explained from first principles — not a lecture hall.",
  },
  {
    phase: "In the class",
    icon: PenLine,
    title: "Guided practice & exam technique",
    desc: "We work problems together, then build the exam technique that turns understanding into marks.",
  },
  {
    phase: "After the class",
    icon: Video,
    title: "Recording, solutions & homework",
    desc: "The full recording, worked solutions and weekly homework are uploaded straight after the session.",
  },
  {
    phase: "After the class",
    icon: TrendingUp,
    title: "Feedback & progress tracking",
    desc: "Work gets marked with specific feedback, and we track progress so the next lesson targets the real gaps.",
  },
];

export const LearningJourney = () => (
  <section className="py-20 px-6 bg-white dark:bg-[#0f1117]" data-testid="learning-journey-section">
    <div className="max-w-6xl mx-auto">
      <Reveal className="max-w-3xl mb-12">
        <Badge className="bg-brand-gold/10 text-brand-gold mb-4 font-body font-semibold px-3 py-1 rounded-full">
          The learning journey
        </Badge>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
          How we teach every single lesson
        </h2>
        <p className="text-slate-600 dark:text-slate-400 font-body text-base md:text-lg">
          Every class follows the same proven structure — from what lands in your inbox before the session, to the
          feedback that shapes the next one.
        </p>
      </Reveal>

      <div className="relative">
        <div className="hidden lg:block absolute left-0 right-0 top-[42px] h-px bg-gradient-to-r from-transparent via-brand-purple/30 to-transparent" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              className="relative bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6 card-hover"
              data-testid={`journey-step-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-brand-purple-soft dark:bg-brand-purple/20 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-brand-purple dark:text-brand-purple-light" />
                </div>
                <span className="font-heading text-3xl font-bold text-slate-100 dark:text-slate-800 leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <p className="text-[11px] uppercase tracking-wider text-brand-gold font-body font-semibold mb-1.5">
                {s.phase}
              </p>
              <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-2">{s.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default LearningJourney;
